import React, { useEffect, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import { AdminFormPageShimmer } from '../common/Shimmer.jsx';
import { devError } from '../../utils/logger.js';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import {
  USER_HUB_NAV_ITEM_LABELS,
  createDefaultUserHubNavVisibility,
  normalizeNavVisibilityFromApi,
  countVisibleNavItems,
} from '../../config/userHubNavItems.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminHubSettings.css';

/**
 * ハブ設定画面（β版フラグ・試用日数等）
 */
const AdminHubSettings = ({ onUpdate }) => {
  const adminRepo = AdminRepository.getInstance();
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isBetaPeriod, setIsBetaPeriod] = useState(false);
  const [trialPeriodDays, setTrialPeriodDays] = useState(14);
  const [betaTrialPeriodDays, setBetaTrialPeriodDays] = useState(30);
  const [pastDueGraceDays, setPastDueGraceDays] = useState(7);
  const [emailTestOverrideTo, setEmailTestOverrideTo] = useState('');
  const [emailOverrideEnabledInProd, setEmailOverrideEnabledInProd] = useState(false);
  const [emailOverrideStatus, setEmailOverrideStatus] = useState(null);
  const [userHubNavVisibility, setUserHubNavVisibility] = useState(createDefaultUserHubNavVisibility);
  const [saveError, setSaveError] = useState('');
  const [betaExitAlerts, setBetaExitAlerts] = useState([]);

  const loadSettings = async () => {
    probe.start('load');
    setLoading(true);
    setSaveError('');
    try {
      const settings = await adminRepo.getHubSettings();
      setIsBetaPeriod(settings.isBetaPeriod === true);
      setTrialPeriodDays(settings.trialPeriodDays ?? 14);
      setBetaTrialPeriodDays(settings.betaTrialPeriodDays ?? 30);
      setPastDueGraceDays(settings.pastDueGraceDays ?? 7);
      setEmailTestOverrideTo(
        settings.emailTestOverrideTo
        || settings.emailOverrideStatus?.emailTestOverrideToStored
        || ''
      );
      setEmailOverrideEnabledInProd(settings.emailOverrideEnabledInProd === true);
      setEmailOverrideStatus(settings.emailOverrideStatus || null);
      setUserHubNavVisibility(normalizeNavVisibilityFromApi(settings.userHubNavVisibility));
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      devError('ハブ設定取得エラー:', err);
      setSaveError('ハブ設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleToggleBeta = async (e) => {
    const value = e.target.checked;
    const prev = isBetaPeriod;
    setIsBetaPeriod(value);
    setSaving(true);
    setSaveError('');
    setBetaExitAlerts([]);
    try {
      const result = await adminRepo.updateHubSettings({ isBetaPeriod: value });
      if (onUpdate) onUpdate();
      if (result?.betaExitAlerts?.length > 0) {
        setBetaExitAlerts(result.betaExitAlerts);
      } else {
        await showAlert({ message: result?.message || 'ハブ設定を更新しました。', variant: 'success', title: '保存完了' });
      }
    } catch (err) {
      setIsBetaPeriod(prev);
      setSaveError(err.message || 'ハブ設定の更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNumericSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setBetaExitAlerts([]);
    try {
      const result = await adminRepo.updateHubSettings({
        trialPeriodDays: Number(trialPeriodDays),
        betaTrialPeriodDays: Number(betaTrialPeriodDays),
        pastDueGraceDays: Number(pastDueGraceDays),
      });
      if (onUpdate) onUpdate();
      await showAlert({ message: result?.message || '数値設定を保存しました。', variant: 'success', title: '保存完了' });
    } catch (err) {
      setSaveError(err.message || '設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const result = await adminRepo.updateHubSettings({
        emailTestOverrideTo: emailTestOverrideTo.trim(),
        emailOverrideEnabledInProd,
      });
      if (onUpdate) onUpdate();
      setEmailTestOverrideTo(result.emailTestOverrideTo || '');
      setEmailOverrideEnabledInProd(result.emailOverrideEnabledInProd === true);
      setEmailOverrideStatus(result.emailOverrideStatus || null);
      await showAlert({ message: result?.message || 'メールオーバーライド設定を保存しました。', variant: 'success', title: '保存完了' });
    } catch (err) {
      setSaveError(err.message || 'メール設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const setNavItemVisible = (env, itemId, visible) => {
    setUserHubNavVisibility((prev) => ({
      ...prev,
      [env]: {
        ...prev[env],
        [itemId]: visible,
      },
    }));
  };

  const setAllNavVisible = (env, visible) => {
    setUserHubNavVisibility((prev) => {
      const nextEnv = { ...prev[env] };
      for (const { id } of USER_HUB_NAV_ITEM_LABELS) {
        nextEnv[id] = visible;
      }
      return { ...prev, [env]: nextEnv };
    });
  };

  const handleSaveNavVisibility = async (e) => {
    e.preventDefault();
    if (countVisibleNavItems(userHubNavVisibility.prod) < 1
      || countVisibleNavItems(userHubNavVisibility.dev) < 1) {
      setSaveError('本番・開発それぞれで、少なくとも 1 つのメニュー項目を表示にしてください。');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const result = await adminRepo.updateHubSettings({
        userHubNavVisibility,
      });
      if (onUpdate) onUpdate();
      setUserHubNavVisibility(normalizeNavVisibilityFromApi(result.userHubNavVisibility));
      await showAlert({
        message: result?.message || 'UserHub メニュー表示設定を保存しました。',
        variant: 'success',
        title: '保存完了',
      });
    } catch (err) {
      setSaveError(err.message || 'メニュー表示設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const formatTimestamp = (ms) => {
    if (!ms) return '-';
    const d = new Date(ms);
    return d.toLocaleDateString('ja-JP');
  };

  const settingsStoredLabel = (status) => {
    if (status?.emailTestOverrideToStored) return status.emailTestOverrideToStored;
    return '（未設定）';
  };

  const overrideSourceLabel = (source) => {
    if (source === 'firestore') return 'Firestore（hub-settings）';
    if (source === 'env-fallback') return '環境変数（移行期フォールバック）';
    return 'なし';
  };

  const renderNavEnvColumn = (env, title) => (
    <div className="admin-hub-nav-env-column">
      <div className="admin-hub-nav-env-header">
        <h3>{title}</h3>
        <div className="admin-hub-nav-env-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={() => setAllNavVisible(env, true)}
          >
            すべて表示
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={() => setAllNavVisible(env, false)}
          >
            すべて非表示
          </button>
        </div>
      </div>
      <ul className="admin-hub-nav-item-list">
        {USER_HUB_NAV_ITEM_LABELS.map(({ id, label }) => (
          <li key={`${env}-${id}`}>
            <label className="admin-hub-settings-beta-toggle">
              <input
                type="checkbox"
                checked={userHubNavVisibility[env]?.[id] !== false}
                onChange={(e) => setNavItemVisible(env, id, e.target.checked)}
                disabled={saving}
              />
              <span>{label}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="admin-list-container">
      <div className="list-header">
        <div className="section-header">
          <h1>ハブ設定</h1>
          <p>User Portal の動作に関する設定を管理します。Firestore config/hub-settings に保存されます。</p>
        </div>
        <button onClick={loadSettings} className="btn btn-secondary" disabled={loading}>
          再読み込み
        </button>
      </div>

      {loading ? (
        <AdminFormPageShimmer fields={5} withHeader={false} />
      ) : (
        <div className="admin-hub-settings-content">
          <div className="form-group">
            <label className="admin-hub-settings-beta-toggle">
              <input
                type="checkbox"
                checked={isBetaPeriod}
                onChange={handleToggleBeta}
                disabled={saving}
              />
              <span>β版期間中にする</span>
            </label>
            <p className="admin-hub-settings-beta-hint">
              ON: 決済・請求メニューを無効化し、ライセンス契約時にモーダルを迂回して β 用試用日数の試用ライセンスを自動付与します。
            </p>
          </div>

          <form onSubmit={handleSaveNumericSettings} className="admin-hub-settings-numeric-form">
            <h2>試用・決済猶予（日数）</h2>
            <p className="form-hint-block">
              試用日数は新規契約・招待特典の基準です（特典日数は加算）。past_due 猶予は決済失敗後、ライセンス停止までの日数です。
            </p>
            <div className="admin-hub-settings-grid">
              <div className="form-group">
                <label htmlFor="trialPeriodDays">本番期の試用日数</label>
                <input
                  id="trialPeriodDays"
                  type="number"
                  min={1}
                  max={365}
                  value={trialPeriodDays}
                  onChange={(e) => setTrialPeriodDays(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="betaTrialPeriodDays">β期間の試用日数</label>
                <input
                  id="betaTrialPeriodDays"
                  type="number"
                  min={1}
                  max={365}
                  value={betaTrialPeriodDays}
                  onChange={(e) => setBetaTrialPeriodDays(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pastDueGraceDays">past_due 決済猶予日数</label>
                <input
                  id="pastDueGraceDays"
                  type="number"
                  min={1}
                  max={365}
                  value={pastDueGraceDays}
                  onChange={(e) => setPastDueGraceDays(e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中...' : '日数設定を保存'}
            </button>
          </form>

          <form onSubmit={handleSaveNavVisibility} className="admin-hub-settings-nav-form">
            <h2>UserHub 機能表示／非表示</h2>
            <p className="form-hint-block">
              UserHub 左メニュー各項目の表示を、本番環境と開発環境で個別に切り替えます。
              非表示にした項目はメニューから消え、URL 直アクセスも拒否されます。
              β版期間中は、この設定とは別に決済・サポート系の既存制限が適用されます。
            </p>
            <div className="admin-hub-nav-env-grid">
              {renderNavEnvColumn('prod', '本番環境 (prod)')}
              {renderNavEnvColumn('dev', '開発環境 (dev)')}
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中...' : 'メニュー表示設定を保存'}
            </button>
          </form>

          <form onSubmit={handleSaveEmailSettings} className="admin-hub-settings-email-form">
            <h2>メール送信オーバーライド</h2>
            <p className="form-hint-block">
              検証期間中は全メールの送信先をここで指定したアドレスに集約します（AdminHub / UserHub 共通）。
              dev 環境では常に適用され、件名に <code>[STAGING]</code> が付きます。
              本番では「本番でも有効」を ON にした場合のみ適用されます。送信先は保存したまま、チェックだけで ON/OFF できます。
            </p>

            {emailOverrideStatus && (
              <div className={`admin-hub-email-status${emailOverrideStatus.activeInCurrentDeployEnv ? ' active' : ''}`}>
                <p className="admin-hub-email-status-title">現在の適用状況（この AdminHub サーバー）</p>
                <dl className="admin-hub-email-status-grid">
                  <div>
                    <dt>Firestore 保存値</dt>
                    <dd>{settingsStoredLabel(emailOverrideStatus)}</dd>
                  </div>
                  <div>
                    <dt>実際に適用中</dt>
                    <dd>
                      {emailOverrideStatus.activeInCurrentDeployEnv
                        ? emailOverrideStatus.effectiveOverrideTo
                        : 'なし（実ユーザー宛に送信）'}
                    </dd>
                  </div>
                  <div>
                    <dt>参照元</dt>
                    <dd>{overrideSourceLabel(emailOverrideStatus.overrideSource)}</dd>
                  </div>
                  <div>
                    <dt>本番オーバーライド許可</dt>
                    <dd>{emailOverrideStatus.emailOverrideEnabledInProd ? 'ON' : 'OFF'}</dd>
                  </div>
                  <div>
                    <dt>デプロイ環境</dt>
                    <dd>{emailOverrideStatus.deployEnv || '—'}</dd>
                  </div>
                </dl>
                {emailOverrideStatus.overrideSource === 'env-fallback' && (
                  <p className="admin-hub-email-status-note">
                    環境変数フォールバックが有効です。下のフォームに保存すると Firestore に移行されます。
                  </p>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="emailTestOverrideTo">送信先オーバーライド</label>
              <input
                id="emailTestOverrideTo"
                type="email"
                value={emailTestOverrideTo}
                onChange={(e) => setEmailTestOverrideTo(e.target.value)}
                placeholder="test@example.com（空欄＝本番は実ユーザーへ送信）"
                disabled={saving}
              />
            </div>
            <label className="admin-hub-settings-beta-toggle admin-hub-settings-email-toggle">
              <input
                type="checkbox"
                checked={emailOverrideEnabledInProd}
                onChange={(e) => setEmailOverrideEnabledInProd(e.target.checked)}
                disabled={saving}
              />
              <span>本番環境でもオーバーライドを有効にする</span>
            </label>
            <p className="admin-hub-settings-beta-hint">
              本番で検証用に集約する場合は ON、実ユーザーへ送る場合は OFF にしてください。送信先アドレスは空にしなくても切り替えできます。
            </p>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中...' : 'メール設定を保存'}
            </button>
          </form>

          {saveError && (
            <div style={{ color: 'var(--danger, #dc3545)', marginTop: '12px' }}>{saveError}</div>
          )}

          {betaExitAlerts.length > 0 && (
            <div
              style={{
                marginTop: '16px',
                padding: '12px 16px',
                border: '1px solid var(--danger, #dc3545)',
                background: 'rgba(220, 53, 69, 0.08)',
                borderRadius: '6px'
              }}
            >
              <h3 style={{ margin: '0 0 8px', color: 'var(--danger, #dc3545)', fontSize: '1rem' }}>
                要注意: 本番期への切替により請求が発生するクリニックがあります
              </h3>
              <p style={{ margin: '0 0 8px', fontSize: '0.875rem' }}>
                以下の <strong>{betaExitAlerts.length}</strong> 件は支払い方法が未登録のまま
                試用 / 支払猶予で利用中です。本番期に切り替わった後、試用終了または猶予期限到来時に
                請求ができず契約キャンセル扱いとなる恐れがあります。
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem' }}>
                {betaExitAlerts.map((a) => (
                  <li key={a.clinicId} style={{ marginBottom: '4px' }}>
                    <strong>{a.clinicName || '(無名)'}</strong>
                    <span style={{ color: 'var(--text-muted, #666)', marginLeft: '6px' }}>[{a.clinicId}]</span>
                    <ul style={{ margin: '2px 0 0', paddingLeft: '16px', color: 'var(--text-muted, #555)' }}>
                      {a.contracts.map((c, i) => (
                        <li key={`${a.clinicId}-${i}`}>
                          {c.licenseName} / status={c.status}
                          {c.trialEndAt && ` / 試用終了: ${formatTimestamp(c.trialEndAt)}`}
                          {c.billingDeferralUntil && ` / 猶予終了: ${formatTimestamp(c.billingDeferralUntil)}`}
                          {c.billingDeferralReason && ` (${c.billingDeferralReason})`}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <p style={{ margin: '8px 0 0', fontSize: '0.813rem', color: 'var(--text-muted, #555)' }}>
                ※ β設定は更新されています。必要に応じて各クリニックに PM 登録を依頼してください。
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminHubSettings;
